// Loads the Google Maps JavaScript API for the solar widget (docs/12-solar-roof-scan.md §7.2).
//
// Cost rules (§4.4): the API is loaded only when the homeowner finishes the address
// step (the address step has no map), once per page however many widgets there are,
// and the 3D library ("maps3d") only on the first "View in 3D".
//
// The key is the browser key from the session response (restricted to the Maps
// JavaScript API, with a daily quota). Solar and Geocoding calls never use it: they
// go through our API with the server key.

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    google?: any;
    __xqsMapsReady?: () => void;
  }
}

let loading: Promise<any> | null = null;

/** The global `google.maps` namespace, loading the script on first use. */
export function loadMaps(browserKey: string | undefined): Promise<any> {
  if (window.google?.maps?.Map) return Promise.resolve(window.google.maps);
  if (loading) return loading;
  loading = new Promise((resolve, reject) => {
    window.__xqsMapsReady = () => resolve(window.google.maps);
    const s = document.createElement("script");
    const key = browserKey ? `key=${encodeURIComponent(browserKey)}&` : "";
    s.src = `https://maps.googleapis.com/maps/api/js?${key}v=weekly&libraries=geometry&loading=async&callback=__xqsMapsReady`;
    s.async = true;
    s.onerror = () => {
      loading = null; // allow a retry on the next attempt
      reject(new Error("The map couldn't load. Check your connection and try again."));
    };
    document.head.appendChild(s);
  });
  return loading;
}

/** The 3D map classes, loaded on the first "View in 3D" (after loadMaps). */
export async function loadMaps3d(): Promise<{ Map3DElement: any; Polygon3DElement: any }> {
  const maps = window.google?.maps;
  if (!maps?.importLibrary) throw new Error("3D view isn't available for this property.");
  return maps.importLibrary("maps3d");
}

/** Wait (for the map fly-in steps). */
export const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
